/**
 * @theme/events — shared event bus glueing components together.
 *
 * One module-scoped EventTarget shared by every component on the page.
 * Components never call each other directly: publishers fire an event,
 * subscribers react. Payload contracts live in docs/architecture.md.
 *
 * Usage:
 *   import { publish, subscribe, CART_UPDATED } from '@theme/events';
 *   const off = subscribe(CART_UPDATED, (event) => render(event.detail.cart));
 *   publish(CART_UPDATED, { cart });
 *   off(); // unsubscribe
 */

const bus = new EventTarget();

/*
 * Event names and their `detail` payloads:
 *   CART_UPDATED    { cart }               — after any successful cart mutation
 *   VARIANT_CHANGED { variant, sectionId } — after a new variant is resolved
 *   DRAWER_OPEN     { id }                 — a drawer/dialog was opened
 */
export const CART_UPDATED = 'cart:updated';
export const VARIANT_CHANGED = 'variant:changed';
export const DRAWER_OPEN = 'drawer:open';

/**
 * Publish `event` on the shared bus with an optional `detail` payload.
 * @param {string} event
 * @param {*} [detail]
 */
export function publish(event, detail) {
  bus.dispatchEvent(new CustomEvent(event, { detail }));
}

/**
 * Subscribe `handler` to `event`. Returns an unsubscribe function —
 * components should call it (or use ThemeComponent's listen(bus, ...))
 * so handlers never outlive their element.
 * @param {string} event
 * @param {(event: CustomEvent) => void} handler
 * @returns {() => void}
 */
export function subscribe(event, handler) {
  bus.addEventListener(event, handler);
  return () => bus.removeEventListener(event, handler);
}
